import { useCallback, useEffect, useMemo, useState } from 'react';
import { Hash, Loader2, Pencil, Plus, Trash2, X } from 'lucide-react';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Separator } from '../../components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { useToast } from '../../../contexts/ToastContext';
import { useUiLocale } from '../../../contexts/UiLocaleContext';
import { subscriberMeApi } from '../../../api/subscriberMeApi';
import { LANGUAGE_OPTIONS, type LanguageCode } from '../../components/LanguageFlag';
import {
  MODERATOR_SUBSCRIBER_TIMEZONES,
  SEND_TIME_OPTIONS,
} from '../../data/subscriberEmailTemplatesMock';
import { cn } from '../../components/ui/utils';

const TOPIC_COLOR_PALETTE = [
  'bg-[#EFF6FF] text-[#1E40AF] border-[#BFDBFE]',
  'bg-[#F0F9FF] text-[#0369A1] border-[#BAE6FD]',
  'bg-[#EEF2FF] text-[#3730A3] border-[#C7D2FE]',
  'bg-[#ECFDF5] text-[#047857] border-[#A7F3D0]',
  'bg-[#FFF7ED] text-[#C2410C] border-[#FED7AA]',
  'bg-[#FDF2F8] text-[#9D174D] border-[#FBCFE8]',
  'bg-[#FFFBEB] text-[#B45309] border-[#FDE68A]',
];

const BUZZWORD_COLORS = [
  'bg-[#EFF6FF] text-[#2563EB] border-[#BFDBFE]',
  'bg-[#F0FDF4] text-[#15803D] border-[#BBF7D0]',
  'bg-[#EEF2FF] text-[#4338CA] border-[#C7D2FE]',
  'bg-[#F8FAFC] text-[#475569] border-[#E2E8F0]',
  'bg-[#F0F9FF] text-[#0284C7] border-[#BAE6FD]',
  'bg-[#FFFBEB] text-[#B45309] border-[#FDE68A]',
  'bg-[#ECFDF5] text-[#0F766E] border-[#99F6E4]',
];

const snInput =
  'h-9 rounded-[10px] border-[#E2E8F0] bg-white text-[13px] text-[#0F172A] shadow-none transition-[border-color,box-shadow] duration-150 focus-visible:border-[#2563EB] focus-visible:ring-2 focus-visible:ring-[#2563EB]/30';
const snPlusBtn =
  'h-9 w-9 shrink-0 rounded-[10px] bg-[#2563EB] p-0 text-white shadow-none transition-[background-color,transform] duration-150 hover:bg-[#1D4ED8] active:scale-[0.97]';
const snOutlineBtn =
  'h-8 rounded-[10px] border-[#E2E8F0] bg-white text-xs font-medium text-[#0F172A] shadow-none transition-[background-color,border-color] duration-150 hover:border-[#BFDBFE] hover:bg-[#F8FAFC]';
const snRow = 'flex items-center justify-between gap-3 text-[14px] leading-snug';
const snLabel = 'shrink-0 text-[#64748B]';
const snValue = 'min-w-0 truncate text-right font-medium text-[#0F172A]';
const snBadgeSuccess =
  'inline-flex items-center rounded-full bg-[#DCFCE7] px-2.5 py-0.5 text-xs font-medium text-[#15803D]';
const snSectionTitle = 'text-[13px] font-semibold tracking-wide text-[#0F172A]';
const snDivider = 'my-1 border-[#E2E8F0]';

type TopicSource = 'own' | 'subscribed';

type TopicRow = {
  id: number;
  name: string;
  color: string;
  buzzwords: string[];
  source: TopicSource;
};

type BuzzwordIdMap = Record<number, Record<string, number>>;

type TemplateOption = { id: number; name: string };

type ToastApi = {
  showSuccess: (message: string, duration?: number) => void;
  showError: (message: string, duration?: number) => void;
};

function asArray<T>(data: unknown): T[] {
  if (Array.isArray(data)) return data as T[];
  if (data && typeof data === 'object' && Array.isArray((data as { items?: unknown }).items)) {
    return (data as { items: T[] }).items;
  }
  return [];
}

function readTopicId(row: Record<string, unknown>): number {
  const raw = row.topicId ?? row.topic_id;
  return Number(raw);
}

function normalizeSendTime(raw: string | null | undefined): string {
  const value = String(raw ?? '09:00').trim();
  const match = value.match(/^(\d{1,2}):(\d{2})/);
  if (!match) return '09:00';
  const h = Math.min(23, Math.max(0, Number(match[1])));
  const m = Math.min(59, Math.max(0, Number(match[2])));
  // Prefer half-hour slots used by the select.
  const rounded = m < 15 ? 0 : m < 45 ? 30 : 0;
  const hour = m >= 45 ? (h + 1) % 24 : h;
  const slot = `${String(hour).padStart(2, '0')}:${String(rounded).padStart(2, '0')}`;
  return SEND_TIME_OPTIONS.includes(slot) ? slot : '09:00';
}

function parseBuzzwordEntries(
  entries: unknown,
): { words: string[]; idMap: Record<string, number> } {
  const words: string[] = [];
  const idMap: Record<string, number> = {};
  if (!Array.isArray(entries)) return { words, idMap };
  for (const entry of entries) {
    if (typeof entry === 'string') {
      const word = entry.trim();
      if (word) words.push(word);
      continue;
    }
    if (!entry || typeof entry !== 'object') continue;
    const row = entry as Record<string, unknown>;
    const word = String(row.buzzword ?? row.word ?? '').trim();
    if (!word) continue;
    words.push(word);
    if (row.id != null && Number.isFinite(Number(row.id))) {
      idMap[word] = Number(row.id);
    }
  }
  return { words, idMap };
}

function langLabel(code: string | null | undefined): string {
  const raw = String(code ?? 'de').toLowerCase();
  if (Object.prototype.hasOwnProperty.call(LANGUAGE_OPTIONS, raw)) {
    return raw.toUpperCase();
  }
  return 'DE';
}

function tzAbbr(tz: string | null | undefined): string {
  if (!tz) return 'CET';
  const map: Record<string, string> = {
    'Europe/Berlin': 'CET',
    'Europe/London': 'GMT',
    UTC: 'UTC',
    'America/New_York': 'EST',
    'America/Los_Angeles': 'PST',
    'Asia/Ho_Chi_Minh': 'ICT',
  };
  return map[tz] || tz;
}

function formatLastSent(value: string | null | undefined, localeTag: string, neverLabel: string): string {
  if (!value) return neverLabel;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return neverLabel;
  return d.toLocaleDateString(localeTag, { year: 'numeric', month: 'short', day: 'numeric' });
}

function buzzwordFieldKey(topicId: number): string {
  return `buzz-${topicId}`;
}

type Props = {
  /** `sidebar` fills the Topics tab like moderator selectedSubLive; `card` is a standalone panel. */
  variant?: 'card' | 'sidebar';
};

export function SubscriberSelfDetailPanel({ variant = 'card' }: Props) {
  const { t, localeTag } = useUiLocale();
  const { showSuccess, showError } = useToast() as ToastApi;
  const isSidebar = variant === 'sidebar';

  const [loading, setLoading] = useState(true);
  const [wordsSaving, setWordsSaving] = useState(false);
  const [prefsSaving, setPrefsSaving] = useState(false);
  const [editingDelivery, setEditingDelivery] = useState(false);
  const [profile, setProfile] = useState<{
    mail: string;
    username: string;
    language: LanguageCode | string;
    lastSentAt: string | null;
  } | null>(null);
  const [topics, setTopics] = useState<TopicRow[]>([]);
  const [buzzwordIdMap, setBuzzwordIdMap] = useState<BuzzwordIdMap>({});
  const [templates, setTemplates] = useState<TemplateOption[]>([]);
  const [preferences, setPreferences] = useState<{
    preferredTemplateId: number | null;
    digestFrequency: string;
    sendTime: string;
    timezone: string;
    templateName: string;
  } | null>(null);
  const [draftFrequency, setDraftFrequency] = useState('daily');
  const [draftSendTime, setDraftSendTime] = useState('09:00');
  const [draftTimezone, setDraftTimezone] = useState('Europe/Berlin');
  const [draftTemplateId, setDraftTemplateId] = useState<string>('none');
  const [newBuzzwordByTopicKey, setNewBuzzwordByTopicKey] = useState<Record<string, string>>({});
  const [newTopicName, setNewTopicName] = useState('');

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const settled = await Promise.allSettled([
        subscriberMeApi.getProfile(),
        subscriberMeApi.getPreferences(),
        subscriberMeApi.getOwnTopics(),
        subscriberMeApi.getOwnBuzzwords(),
        subscriberMeApi.getSubscribedTopics(),
        subscriberMeApi.getAvailableTemplates(),
        subscriberMeApi.getNewsletters({ skip: 0, limit: 1 }),
      ]);

      const [
        profileSettled,
        prefsSettled,
        ownTopicsSettled,
        ownBuzzSettled,
        subscribedSettled,
        templatesSettled,
        newslettersSettled,
      ] = settled;

      if (profileSettled.status !== 'fulfilled') {
        setProfile(null);
        showError('Could not load your profile.', 4000);
        return;
      }

      const p = profileSettled.value.data as Record<string, unknown>;
      const nlPage =
        newslettersSettled.status === 'fulfilled'
          ? (newslettersSettled.value.data as { items?: Array<{ sent_at?: string }> })
          : null;
      const lastNl =
        nlPage && Array.isArray(nlPage.items) ? nlPage.items[0] : null;

      setProfile({
        mail: String(p.mail ?? p.email ?? ''),
        username: String(p.username ?? ''),
        language: String(p.language ?? 'de'),
        lastSentAt: lastNl?.sent_at
          ? String(lastNl.sent_at)
          : p.last_sent_at
            ? String(p.last_sent_at)
            : null,
      });

      const templateRows =
        templatesSettled.status === 'fulfilled'
          ? asArray<Record<string, unknown>>(templatesSettled.value.data).map((row) => ({
              id: Number(row.id),
              name: String(row.name ?? t('subscribers.defaultDigest')),
            })).filter((row) => Number.isFinite(row.id))
          : [];
      setTemplates(templateRows);

      if (prefsSettled.status === 'fulfilled') {
        const pref = prefsSettled.value.data as Record<string, unknown>;
        const preferredId =
          pref.preferred_template_id != null ? Number(pref.preferred_template_id) : null;
        const matchedTemplate = templateRows.find((row) => row.id === preferredId);
        const digestFrequency = String(pref.digest_frequency ?? 'daily');
        const sendTime = normalizeSendTime(
          typeof pref.email_send_time === 'string' ? pref.email_send_time : '09:00',
        );
        const timezone = String(pref.timezone ?? 'Europe/Berlin');

        setPreferences({
          preferredTemplateId: Number.isFinite(preferredId as number) ? preferredId : null,
          digestFrequency,
          sendTime,
          timezone,
          templateName: matchedTemplate?.name ?? t('subscribers.defaultDigest'),
        });
        setDraftFrequency(digestFrequency);
        setDraftSendTime(sendTime);
        setDraftTimezone(timezone);
        setDraftTemplateId(
          preferredId != null && Number.isFinite(preferredId) ? String(preferredId) : 'none',
        );
      } else {
        setPreferences({
          preferredTemplateId: null,
          digestFrequency: 'daily',
          sendTime: '09:00',
          timezone: 'Europe/Berlin',
          templateName: t('subscribers.defaultDigest'),
        });
      }

      const ownTopics = ownTopicsSettled.status === 'fulfilled'
        ? asArray<Record<string, unknown>>(ownTopicsSettled.value.data)
        : [];
      const subscribedTopics = subscribedSettled.status === 'fulfilled'
        ? asArray<Record<string, unknown>>(subscribedSettled.value.data)
        : [];
      const ownBuzzList = ownBuzzSettled.status === 'fulfilled'
        ? asArray<Record<string, unknown>>(ownBuzzSettled.value.data)
        : [];

      const idMap: BuzzwordIdMap = {};
      const buzzByTopic: Record<number, string[]> = {};

      const attachBuzzwords = (topicId: number, entries: unknown) => {
        const parsed = parseBuzzwordEntries(entries);
        if (!buzzByTopic[topicId]) buzzByTopic[topicId] = [];
        if (!idMap[topicId]) idMap[topicId] = {};
        for (const word of parsed.words) {
          if (!buzzByTopic[topicId].includes(word)) buzzByTopic[topicId].push(word);
        }
        Object.assign(idMap[topicId], parsed.idMap);
      };

      // Own buzzwords list + any inline arrays on topic rows
      ownBuzzList.forEach((b) => {
        const topicId = readTopicId(b);
        if (!Number.isFinite(topicId)) return;
        attachBuzzwords(topicId, [b]);
      });
      [...ownTopics, ...subscribedTopics].forEach((row) => {
        const topicId = Number(row.id);
        if (!Number.isFinite(topicId)) return;
        if (row.buzzwords != null) attachBuzzwords(topicId, row.buzzwords);
      });

      // Subscribed catalog topics may need a separate buzzword fetch
      const subscribedNeedFetch = subscribedTopics.filter((row) => {
        const topicId = Number(row.id);
        return Number.isFinite(topicId) && !(buzzByTopic[topicId]?.length);
      });
      if (subscribedNeedFetch.length > 0) {
        const buzzResults = await Promise.allSettled(
          subscribedNeedFetch.map((row) =>
            subscriberMeApi.getSubscribedTopicBuzzwords(Number(row.id)),
          ),
        );
        buzzResults.forEach((result, index) => {
          if (result.status !== 'fulfilled') return;
          const topicId = Number(subscribedNeedFetch[index].id);
          attachBuzzwords(topicId, result.value.data);
        });
      }

      const ownIds = new Set(ownTopics.map((row) => Number(row.id)));
      const merged: TopicRow[] = [];
      const pushTopic = (row: Record<string, unknown>, source: TopicSource, index: number) => {
        const id = Number(row.id);
        if (!Number.isFinite(id)) return;
        if (merged.some((t) => t.id === id)) return;
        merged.push({
          id,
          name: String(row.topic ?? row.name ?? ''),
          color: TOPIC_COLOR_PALETTE[index % TOPIC_COLOR_PALETTE.length],
          buzzwords: buzzByTopic[id] ?? [],
          source,
        });
      };

      ownTopics.forEach((row, index) => pushTopic(row, 'own', index));
      subscribedTopics.forEach((row, index) => {
        const id = Number(row.id);
        // Prefer own source if same id appears in both lists
        if (ownIds.has(id)) return;
        pushTopic(row, 'subscribed', ownTopics.length + index);
      });

      setBuzzwordIdMap(idMap);
      setTopics(merged);

      if (
        ownTopicsSettled.status === 'rejected' &&
        subscribedSettled.status === 'rejected'
      ) {
        showError('Could not load topics.', 4000);
      }
    } catch {
      setProfile(null);
      setTopics([]);
      setPreferences(null);
      showError('Could not load your profile.', 4000);
    } finally {
      setLoading(false);
    }
  }, [showError, t]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  const displayName = useMemo(
    () => profile?.username || profile?.mail || t('common.profile'),
    [profile, t],
  );

  const handleAddBuzzword = async (topicId: number) => {
    const fieldKey = buzzwordFieldKey(topicId);
    const word = (newBuzzwordByTopicKey[fieldKey] ?? '').trim();
    if (!word) return;

    setTopics((prev) =>
      prev.map((topic) =>
        topic.id === topicId && !topic.buzzwords.includes(word)
          ? { ...topic, buzzwords: [...topic.buzzwords, word] }
          : topic,
      ),
    );
    setNewBuzzwordByTopicKey((prev) => ({ ...prev, [fieldKey]: '' }));

    setWordsSaving(true);
    try {
      const res = await subscriberMeApi.createOwnBuzzword({ topicId, buzzword: word });
      const newId = (res.data as { id?: number })?.id;
      if (newId) {
        setBuzzwordIdMap((prev) => ({
          ...prev,
          [topicId]: { ...(prev[topicId] ?? {}), [word]: newId },
        }));
      } else {
        await loadData();
      }
      showSuccess(t('subscribers.toastBuzzwordAdded'));
    } catch {
      showError('Could not add buzzword.');
      await loadData();
    } finally {
      setWordsSaving(false);
    }
  };

  const handleRemoveBuzzword = async (topicId: number, word: string) => {
    const buzzwordId = buzzwordIdMap[topicId]?.[word];
    if (buzzwordId == null) return;

    setTopics((prev) =>
      prev.map((topic) =>
        topic.id === topicId
          ? { ...topic, buzzwords: topic.buzzwords.filter((item) => item !== word) }
          : topic,
      ),
    );

    setWordsSaving(true);
    try {
      await subscriberMeApi.deleteOwnBuzzword(buzzwordId);
      showSuccess(t('subscribers.toastBuzzwordRemoved'));
    } catch {
      showError('Could not remove buzzword.');
      await loadData();
    } finally {
      setWordsSaving(false);
    }
  };

  const handleAddTopic = async () => {
    const name = newTopicName.trim();
    if (!name) return;

    if (topics.some((topic) => topic.name.toLowerCase() === name.toLowerCase())) {
      showError(t('subscribers.toastTopicExists'));
      return;
    }

    setWordsSaving(true);
    try {
      await subscriberMeApi.createOwnTopic({ topic: name });
      setNewTopicName('');
      showSuccess(t('subscribers.toastTopicAdded'));
      await loadData();
    } catch {
      showError('Could not add topic.');
    } finally {
      setWordsSaving(false);
    }
  };

  const handleDeleteTopic = async (topic: TopicRow) => {
    setWordsSaving(true);
    try {
      if (topic.source === 'subscribed') {
        await subscriberMeApi.unsubscribeSubscribedTopic(topic.id);
      } else {
        await subscriberMeApi.deleteOwnTopic(topic.id);
      }
      showSuccess('Topic removed.');
      await loadData();
    } catch {
      showError('Could not remove topic.');
    } finally {
      setWordsSaving(false);
    }
  };

  const handleSaveDelivery = async () => {
    if (!preferences) return;
    setPrefsSaving(true);
    try {
      const preferredTemplateId =
        draftTemplateId === 'none' ? null : Number(draftTemplateId);
      await subscriberMeApi.putPreferences({
        preferred_template_id: Number.isFinite(preferredTemplateId as number)
          ? preferredTemplateId
          : null,
        timezone: draftTimezone,
        digest_frequency: draftFrequency,
        email_send_time: draftSendTime,
      });
      const matched = templates.find((row) => row.id === preferredTemplateId);
      setPreferences({
        preferredTemplateId: Number.isFinite(preferredTemplateId as number)
          ? preferredTemplateId
          : null,
        digestFrequency: draftFrequency,
        sendTime: draftSendTime,
        timezone: draftTimezone,
        templateName: matched?.name ?? t('subscribers.defaultDigest'),
      });
      setEditingDelivery(false);
      showSuccess(t('subscriberDetail.toastSettingsSaved') || 'Settings saved.');
    } catch {
      showError('Could not save delivery settings.');
    } finally {
      setPrefsSaving(false);
    }
  };

  const beginEditDelivery = () => {
    setDraftFrequency(preferences?.digestFrequency ?? 'daily');
    setDraftSendTime(preferences?.sendTime ?? '09:00');
    setDraftTimezone(preferences?.timezone ?? 'Europe/Berlin');
    setDraftTemplateId(
      preferences?.preferredTemplateId != null
        ? String(preferences.preferredTemplateId)
        : 'none',
    );
    setEditingDelivery(true);
  };

  if (loading) {
    return (
      <div
        className={cn(
          'flex flex-col items-center justify-center gap-3 px-8 py-16 font-[Inter,Manrope,ui-sans-serif,system-ui,sans-serif]',
          !isSidebar &&
            'w-full max-w-md rounded-[18px] border border-[#E2E8F0] bg-white shadow-[0_8px_30px_rgba(15,23,42,0.06)]',
          isSidebar && 'h-full bg-white',
        )}
      >
        <Loader2 className="size-8 animate-spin text-[#2563EB]" aria-hidden />
        <span className="text-sm text-[#64748B]">{t('common.loading')}</span>
      </div>
    );
  }

  if (!profile) {
    return (
      <div
        className={cn(
          'px-8 py-16 text-center font-[Inter,Manrope,ui-sans-serif,system-ui,sans-serif]',
          !isSidebar &&
            'w-full max-w-md rounded-[18px] border border-[#E2E8F0] bg-white shadow-[0_8px_30px_rgba(15,23,42,0.06)]',
          isSidebar && 'bg-white',
        )}
      >
        <p className="text-sm text-[#64748B]">Could not load your profile.</p>
      </div>
    );
  }

  return (
    <div
      className={cn(
        'overflow-hidden bg-white font-[Inter,Manrope,ui-sans-serif,system-ui,sans-serif]',
        !isSidebar &&
          'w-full max-w-md rounded-[18px] border border-[#E2E8F0] shadow-[0_8px_30px_rgba(15,23,42,0.06)]',
        isSidebar && 'flex h-full min-h-0 flex-col',
      )}
    >
      <div className={cn('border-b border-[#E2E8F0] px-5 pb-3.5 pt-4', isSidebar && 'bg-[#F8FAFC]')}>
        <h2 className="text-[16px] font-semibold text-[#0F172A]">{displayName}</h2>
        <p className="mt-0.5 text-[13px] text-[#64748B]">{t('subscribers.sidebarDetails')}</p>
      </div>

      <div
        className={cn(
          'sn-scroll space-y-5 overflow-y-auto px-5 py-5',
          isSidebar ? 'min-h-0 flex-1' : 'max-h-[min(72vh,720px)]',
        )}
      >
        <section>
          <h3 className={cn(snSectionTitle, 'mb-3')}>{t('common.profile')}</h3>
          <div className="space-y-3">
            <div className={snRow}>
              <span className={snLabel}>{t('common.email')}</span>
              <span className={snValue}>{profile.mail}</span>
            </div>
            <div className={snRow}>
              <span className={snLabel}>{t('common.language')}</span>
              <Badge
                variant="secondary"
                className="rounded-full border border-[#BFDBFE] bg-[#EFF6FF] px-2.5 py-0.5 text-[11px] font-semibold text-[#2563EB]"
              >
                {langLabel(profile.language)}
              </Badge>
            </div>
            <div className={snRow}>
              <span className={snLabel}>{t('common.status')}</span>
              <span className={snBadgeSuccess}>{t('common.active')}</span>
            </div>
            <div className={snRow}>
              <span className={snLabel}>{t('common.memberSince')}</span>
              <span className={snValue}>—</span>
            </div>
          </div>
        </section>

        <Separator className={snDivider} />

        <section>
          <div className="mb-3">
            <h3 className={snSectionTitle}>{t('subscribers.topicsSectionTitle')}</h3>
            <p className="mt-1 text-[12px] leading-relaxed text-[#64748B]">
              {t('subscribers.topicsSectionDesc')}
            </p>
          </div>

          <div className="space-y-3">
            {topics.length === 0 ? (
              <p className="text-[13px] italic text-[#64748B]">{t('subscribers.noTopicsHint')}</p>
            ) : (
              topics.map((topic) => {
                const buzzFieldKey = buzzwordFieldKey(topic.id);
                return (
                  <div
                    key={topic.id}
                    className="rounded-[14px] border border-[#E2E8F0] bg-[#F8FAFC] p-3.5"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <Badge
                        className={`rounded-full border text-[11px] font-medium ${topic.color}`}
                      >
                        {topic.name}
                      </Badge>
                      <button
                        type="button"
                        disabled={wordsSaving}
                        onClick={() => void handleDeleteTopic(topic)}
                        className="rounded-[8px] p-1.5 text-[#94A3B8] transition-colors duration-150 hover:bg-red-50 hover:text-red-500"
                        aria-label={t('subscriberDetail.deleteTopicTitle')}
                        title={t('subscriberDetail.deleteTopicTitle')}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>

                    <p className="mt-3 text-[11px] font-semibold uppercase tracking-wide text-[#64748B]">
                      {t('common.buzzwords')}
                    </p>
                    <div className="mt-1.5 flex flex-wrap gap-1.5">
                      {topic.buzzwords.length === 0 ? (
                        <span className="text-[12px] italic text-[#94A3B8]">
                          {t('subscribers.noBuzzwordsYet')}
                        </span>
                      ) : (
                        topic.buzzwords.map((word, index) => (
                          <span
                            key={word}
                            className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-[11px] font-medium ${BUZZWORD_COLORS[index % BUZZWORD_COLORS.length]}`}
                          >
                            #{word}
                            <button
                              type="button"
                              disabled={wordsSaving}
                              onClick={() => void handleRemoveBuzzword(topic.id, word)}
                              className="text-[#94A3B8] transition-colors duration-150 hover:text-red-500"
                              aria-label={t('subscribers.removeBuzzwordAria', { word })}
                            >
                              <X className="h-2.5 w-2.5" />
                            </button>
                          </span>
                        ))
                      )}
                    </div>
                    <div className="mt-2.5 flex gap-2">
                      <div className="relative min-w-0 flex-1">
                        <Hash className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-[#94A3B8]" />
                        <Input
                          placeholder={t('subscribers.addBuzzwordPlaceholder')}
                          value={newBuzzwordByTopicKey[buzzFieldKey] ?? ''}
                          onChange={(event) =>
                            setNewBuzzwordByTopicKey((prev) => ({
                              ...prev,
                              [buzzFieldKey]: event.target.value,
                            }))
                          }
                          onKeyDown={(event) => {
                            if (event.key === 'Enter') void handleAddBuzzword(topic.id);
                          }}
                          className={cn(snInput, 'pl-8')}
                          disabled={wordsSaving}
                        />
                      </div>
                      <Button
                        type="button"
                        size="sm"
                        className={snPlusBtn}
                        disabled={wordsSaving || !(newBuzzwordByTopicKey[buzzFieldKey] ?? '').trim()}
                        onClick={() => void handleAddBuzzword(topic.id)}
                      >
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                );
              })
            )}

            <div className="rounded-[14px] border border-dashed border-[#BFDBFE] bg-[#F8FBFF] p-3.5">
              <p className="mb-2 text-[12px] font-medium text-[#64748B]">{t('subscribers.addTopic')}</p>
              <div className="flex gap-2">
                <Input
                  placeholder={t('subscribers.topicNamePlaceholder')}
                  value={newTopicName}
                  onChange={(event) => setNewTopicName(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') void handleAddTopic();
                  }}
                  className={snInput}
                  disabled={wordsSaving}
                />
                <Button
                  type="button"
                  size="sm"
                  className={snPlusBtn}
                  disabled={wordsSaving || !newTopicName.trim()}
                  onClick={() => void handleAddTopic()}
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        </section>

        <Separator className={snDivider} />

        <section>
          <div className="mb-3 flex items-center justify-between gap-2">
            <h3 className={snSectionTitle}>{t('subscribers.deliverySettings')}</h3>
            {!editingDelivery ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className={cn(snOutlineBtn, 'gap-1.5')}
                onClick={beginEditDelivery}
              >
                <Pencil className="h-3 w-3" /> {t('common.edit')}
              </Button>
            ) : (
              <div className="flex gap-1.5">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className={snOutlineBtn}
                  disabled={prefsSaving}
                  onClick={() => setEditingDelivery(false)}
                >
                  {t('common.cancel')}
                </Button>
                <Button
                  type="button"
                  size="sm"
                  className="h-8 rounded-[10px] bg-[#2563EB] px-3 text-xs font-medium text-white shadow-none transition-[background-color,transform] duration-150 hover:bg-[#1D4ED8] active:scale-[0.98]"
                  disabled={prefsSaving}
                  onClick={() => void handleSaveDelivery()}
                >
                  {prefsSaving ? <Loader2 className="h-3 w-3 animate-spin" /> : t('common.save')}
                </Button>
              </div>
            )}
          </div>

          {editingDelivery ? (
            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label className="text-[12px] text-[#64748B]">{t('common.frequency')}</Label>
                <Select value={draftFrequency} onValueChange={setDraftFrequency}>
                  <SelectTrigger className={cn(snInput, 'h-9')}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="daily">{t('common.daily')}</SelectItem>
                    <SelectItem value="weekly">Weekly</SelectItem>
                    <SelectItem value="off">{t('common.off')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-[12px] text-[#64748B]">{t('subscribers.sendTime')}</Label>
                <Select value={draftSendTime} onValueChange={setDraftSendTime}>
                  <SelectTrigger className={cn(snInput, 'h-9 font-mono')}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SEND_TIME_OPTIONS.map((slot) => (
                      <SelectItem key={slot} value={slot} className="font-mono">
                        {slot}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-[12px] text-[#64748B]">{t('common.timezone')}</Label>
                <Select value={draftTimezone} onValueChange={setDraftTimezone}>
                  <SelectTrigger className={cn(snInput, 'h-9')}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {!MODERATOR_SUBSCRIBER_TIMEZONES.some((z) => z.value === draftTimezone) ? (
                      <SelectItem value={draftTimezone}>{draftTimezone}</SelectItem>
                    ) : null}
                    {MODERATOR_SUBSCRIBER_TIMEZONES.map((zone) => (
                      <SelectItem key={zone.value} value={zone.value}>
                        {zone.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-[12px] text-[#64748B]">{t('subscribers.emailTemplate')}</Label>
                <Select value={draftTemplateId} onValueChange={setDraftTemplateId}>
                  <SelectTrigger className={cn(snInput, 'h-9')}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">{t('subscribers.defaultDigest')}</SelectItem>
                    {templates.map((tpl) => (
                      <SelectItem key={tpl.id} value={String(tpl.id)}>
                        {tpl.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <div className={snRow}>
                <span className={snLabel}>{t('common.frequency')}</span>
                <span className={snValue}>
                  {preferences?.digestFrequency === 'daily'
                    ? t('common.daily')
                    : preferences?.digestFrequency === 'weekly'
                      ? 'Weekly'
                      : preferences?.digestFrequency === 'off'
                        ? t('common.off')
                        : '—'}
                </span>
              </div>
              <div className={snRow}>
                <span className={snLabel}>{t('subscribers.sendTime')}</span>
                <span className={snValue}>{preferences?.sendTime ?? '—'}</span>
              </div>
              <div className={snRow}>
                <span className={snLabel}>{t('common.timezone')}</span>
                <span className={snValue}>
                  {preferences
                    ? `${tzAbbr(preferences.timezone)} (${preferences.timezone})`
                    : '—'}
                </span>
              </div>
              <div className={snRow}>
                <span className={snLabel}>{t('subscribers.emailTemplate')}</span>
                <span className={snValue}>
                  {preferences?.templateName ?? t('subscribers.defaultDigest')}
                </span>
              </div>
            </div>
          )}
        </section>

        <Separator className={snDivider} />

        <section>
          <h3 className={cn(snSectionTitle, 'mb-3')}>{t('subscribers.deliveryHistory')}</h3>
          <div className="space-y-3">
            <div className={snRow}>
              <span className={snLabel}>{t('subscribers.lastSent')}</span>
              <span className={snValue}>
                {formatLastSent(profile.lastSentAt, localeTag, t('common.never'))}
              </span>
            </div>
            <div className={snRow}>
              <span className={snLabel}>{t('subscribers.lastStatus')}</span>
              {profile.lastSentAt ? (
                <span className={snBadgeSuccess}>{t('common.success')}</span>
              ) : (
                <span className={snValue}>—</span>
              )}
            </div>
            <div className={snRow}>
              <span className={snLabel}>{t('subscribers.lastError')}</span>
              <span className={snValue}>—</span>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
